import React from 'react'
import classNames from 'classnames'
import { PricingCardProps } from './types.d'
import { Card, Text } from '@toasttab/shared/src/utils/craftUtils'
import Typography from '@toasttab/shared/src/components/atoms/Typography/Typography'
import Button from '@toasttab/shared/src/components/atoms/Button/Button'
import Badge from '@toasttab/shared/src/components/atoms/Badge/Badge'
import TextLink from '@toasttab/shared/src/components/molecules/TextLink/TextLink'
import { Icon } from '@toasttab/shared/src/utils/craftUtils'
import StyledList from '@toasttab/shared/src/components/molecules/StyledList/StyledList'

const PricingCard = ({
  accentColor,
  accentPosition,
  badgeColor,
  badgeText,
  children, // styledList
  listHeader,
  ctaLabel,
  ctaLink,
  padding,
  packagePrice,
  packagePriceMonthly = '',
  pricePrefix,
  priceSuffix,
  sectionDescription,
  sectionHeading,
  bottomLink,
  textAlignment,
  fourCardLayout = false,
  dataTestId = 'PricingCard-1'
}: PricingCardProps) => {
  const containerClasses = classNames(
    'bg-white relative rounded-md border border-card-light pricing-card h-full',
    {
      flex: bottomLink
    }
  )
  const pricingCardClasses = classNames({
    'box-border gap-y-4': true,
    '!p-6': !bottomLink,
    'inline-grid !px-6 !pb-4 !pt-6': bottomLink,
    'lg:grid-rows-[minmax(auto,310px)_auto]': fourCardLayout && !bottomLink
  })
  const topSectionClasses = classNames({
    'inline-grid gap-y-4 grid-rows[minmax(auto,272px)_auto] md:grid-rows-[minmax(auto,252px)_auto]':
      packagePrice && packagePrice.trim() !== ''
  })

  const accentClasses = classNames({
    'accent-top absolute h-4 rounded-tl-[8px] rounded-tr-[8px] -top-[1px] -left-[1px] -right-[1px] before:content-[""] before:absolute before:top-0 before:block before:w-full before:h-1 overflow-hidden':
      accentPosition === 'top',
    'accent-bottom absolute h-4 rounded-bl-[8px] rounded-br-[8px] -bottom-[1px] -left-[1px] -right-[1px] before:content-[""] before:absolute before:bottom-0 before:block before:w-full before:h-1 overflow-hidden':
      accentPosition === 'bottom',

    'before:bg-blue-75': accentColor === 'blue-75',
    'before:bg-yellow-50': accentColor === 'yellow-50',
    'before:bg-brand-50': accentColor === 'brand-50'
  })
  const innerClasses = classNames(
    Card.cardPadding(padding),
    Text.alignment(textAlignment),
    pricingCardClasses
  )

  const convertSymbolsToWords = (text: string): string => {
    if (!text) return text

    // Replace symbols with words
    let converted = text.replace(/\//g, ' per ').replace(/\+/g, ' plus ')

    // Remove any double spaces
    converted = converted.replace(/\s+/g, ' ').trim()

    return converted
  }

  const listItems: any[] = []
  if (Array.isArray(children)) {
    children.forEach((child: any) => {
      if (Array.isArray(child.listItems) && child.listItems.length > 0) {
        listItems.push({
          ...child.listItems[0],
          listStyles: child.listStyles
        })
      }
    })
  }

  return (
    <div className={containerClasses} data-testid={dataTestId}>
      {accentPosition === 'top' && (
        <div className={accentClasses} data-testid='PricingCard-accent--top' />
      )}
      <div className={innerClasses} data-testid='PricingCard-inner'>
        <div className='inline-grid content-between'>
          <div className={topSectionClasses}>
            <div className='inline-grid'>
              {badgeText && badgeColor ? (
                <div className='absolute -top-[0.875rem] left-0 right-0 text-center'>
                  <Badge
                    badgeColor={badgeColor}
                    classes='!font-normal'
                    text={badgeText}
                    uppercase
                  />
                </div>
              ) : null}
              <div>
                {sectionHeading ? (
                  <div className='mb-6 border-b border-b-gray-50'>
                    <Typography
                      as='h2'
                      classes='mb-4 text-[22px] sm:text-[26px] !font-normal'
                      variation='h4'
                    >
                      {sectionHeading}
                    </Typography>
                  </div>
                ) : null}
                {sectionDescription ? (
                  <Typography
                    as='p'
                    classes='text-secondary'
                    variation='default'
                  >
                    {sectionDescription}
                  </Typography>
                ) : null}
              </div>
              <div className='self-center'>
                {pricePrefix ? (
                  <Typography
                    as='p'
                    classes='text-secondary !text-xs'
                    variation='default'
                  >
                    {pricePrefix}
                  </Typography>
                ) : null}
                {packagePrice ? (
                  <p>
                    <Typography
                      as='span'
                      classes='!text-[31px] sm:!text-[2.813rem] !leading-[40px] sm:!leading-[45px] !text-default !font-effra'
                      variation='default'
                    >
                      {packagePrice}
                    </Typography>
                    {packagePriceMonthly ? (
                      <Typography
                        ariaLabel={convertSymbolsToWords(packagePriceMonthly)}
                        as='span'
                        classes='text-secondary'
                        variation='default'
                      >
                        {packagePriceMonthly}
                      </Typography>
                    ) : null}
                  </p>
                ) : null}
                {priceSuffix ? (
                  <Typography
                    ariaLabel={convertSymbolsToWords(priceSuffix)}
                    as='p'
                    classes='text-secondary'
                    variation='default'
                  >
                    {priceSuffix}
                  </Typography>
                ) : null}
              </div>
            </div>
            <div className='mt-2'>
              {ctaLabel && ctaLink ? (
                <Button
                  classes='mb-8'
                  fontWeight={['font-semibold']}
                  href={ctaLink}
                  size='medium'
                  variation='primary'
                  fullwidth={
                    packagePrice && packagePrice.trim() !== '' ? false : true
                  }
                >
                  {ctaLabel}
                </Button>
              ) : null}
              {listHeader ? (
                <>
                  <div>
                    <Typography
                      as='p'
                      classes='text-secondary'
                      variation='default'
                    >
                      {listHeader}
                    </Typography>
                  </div>
                  <div className='mt-3 mb-6 border-b border-b-gray-30' />
                </>
              ) : null}
              {listItems.length > 0 && (
                <StyledList
                  listItems={listItems}
                  listOnly
                  listStyles='none'
                  listVariation='heading-only'
                  sectionDescription=''
                  sectionHeading=''
                />
              )}
            </div>
          </div>
        </div>
        {bottomLink ? (
          <div className='mt-5 pt-2 border-t border-t-gray-30 self-end'>
            <TextLink
              classes='mt-2'
              href={bottomLink.url}
              icon={Icon.linkIcons(bottomLink.icon)}
              text={bottomLink.label}
              description={`${sectionHeading ? sectionHeading : 'Link for '} ${
                bottomLink.label
              }`}
            />
          </div>
        ) : null}
      </div>
      {accentPosition === 'bottom' && (
        <div
          className={accentClasses}
          data-testid='PricingCard-accent--bottom'
        />
      )}
    </div>
  )
}

export default PricingCard
